import { entryWith, isStaticSite } from "./plan";

/**
 * Comando do "Executar" quando o projeto não definiu um: lido dos arquivos (package.json,
 * requirements.txt, manage.py…).
 *
 *  - local: o terminal na máquina da pessoa. Usa `python -m …` (no Windows, a pasta Scripts nem
 *    sempre está no PATH) e o gerenciador de pacotes do projeto.
 *  - sandbox: ambiente novo na nuvem, com a porta de preview em $PORT e escutando em 0.0.0.0.
 */
export type RunTarget = "local" | "sandbox";

interface Pkg {
  scripts?: Record<string, string>;
  dependencies?: Record<string, string>;
  devDependencies?: Record<string, string>;
  main?: string;
}

export function detectRunCommand(
  files: Record<string, string>,
  target: RunTarget,
  platform: "win32" | "darwin" | "linux" = "linux",
): string | null {
  const py = target === "local" && platform === "win32" ? "python" : "python3";
  // Site estático (HTML, CSS e JS de página): no sandbox, um servidor de arquivos na porta de
  // preview; na máquina, quem chama abre o Live Server (aqui: null).
  if (isStaticSite(files)) return target === "sandbox" ? `${py} -m http.server $PORT --bind 0.0.0.0` : null;
  if ("package.json" in files) {
    let pkg: Pkg = {};
    try {
      pkg = JSON.parse(files["package.json"] ?? "{}") as Pkg;
    } catch {
      /* package.json inválido: segue com o padrão */
    }
    const deps = { ...pkg.dependencies, ...pkg.devDependencies };
    const scripts = pkg.scripts ?? {};
    const pm =
      "pnpm-lock.yaml" in files
        ? "pnpm"
        : "yarn.lock" in files
          ? "yarn"
          : "bun.lockb" in files
            ? "bun"
            : "npm";
    const install = `${pm} install`;
    const run = (name: string, extra = "") =>
      `${pm} run ${name}${extra ? (pm === "npm" ? ` -- ${extra}` : ` ${extra}`) : ""}`;
    const sandbox = target === "sandbox";
    if (scripts.dev && deps.vite)
      return `${install} && ${run("dev", sandbox ? "--host 0.0.0.0 --port $PORT" : "")}`;
    if (scripts.dev && deps.next) return `${install} && ${run("dev", sandbox ? "-H 0.0.0.0 -p $PORT" : "")}`;
    if (scripts.dev) return `${install} && ${run("dev")}`;
    if (scripts.start) return `${install} && ${pm} start`;
    return `${install} && node ${pkg.main ?? "index.js"}`;
  }

  const rootPy = Object.keys(files).filter((p) => /^[^/]+\.py$/.test(p));
  if ("requirements.txt" in files || "pyproject.toml" in files || rootPy.length) {
    const deps = `${files["requirements.txt"] ?? ""}\n${files["pyproject.toml"] ?? ""}`.toLowerCase();
    const install =
      "requirements.txt" in files
        ? `${py} -m pip install -r requirements.txt && `
        : "pyproject.toml" in files
          ? `${py} -m pip install -e . && `
          : "";
    const entry = ["main.py", "app.py", "server.py"].find((p) => p in files) ?? rootPy[0] ?? "main.py";
    const mod = entry.replace(/\.py$/, "");
    const host = target === "sandbox" ? " --host 0.0.0.0 --port $PORT" : "";
    const streamlit = entryWith(files, /import streamlit|from streamlit/);
    if (streamlit || /\bstreamlit\b/.test(deps)) {
      const extra =
        target === "sandbox" ? " --server.address 0.0.0.0 --server.port $PORT --server.headless true" : "";
      return `${install}${py} -m streamlit run ${streamlit ?? entry}${extra}`;
    }
    if ("manage.py" in files)
      return `${install}${py} manage.py runserver${target === "sandbox" ? " 0.0.0.0:$PORT" : ""}`;
    if (/\b(fastapi|uvicorn)\b/.test(deps)) return `${install}${py} -m uvicorn ${mod}:app --reload${host}`;
    if (/\bflask\b/.test(deps)) return `${install}${py} -m flask --app ${mod} run --debug${host}`;
    return `${install}${py} ${entry}`;
  }

  if ("go.mod" in files) return "go run .";
  if ("Cargo.toml" in files) return "cargo run";
  if ("pom.xml" in files)
    return /spring-boot/.test(files["pom.xml"] ?? "") ? "mvn spring-boot:run" : "mvn -q compile exec:java";
  if ("index.php" in files) return `php -S ${target === "sandbox" ? "0.0.0.0:$PORT" : "localhost:8000"}`;
  return null;
}
